import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Container,
  IconButton,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import { EmailChip } from "../../AsanaTimeTrackingPage";

// type = "work-intervals" | "user-sla"

export default function Export({
  dateFromValue,
  dateToValue,
  assigneeIdsArray,
  type = "work-intervals",
}) {
  const [spreadsheetUrl, setSpreadsheetUrl] = useState("");
  const [dateFrom, setDateFrom] = useState(dateFromValue);
  const [dateTo, setDateTo] = useState(dateToValue);
  const [assigneeIds, setAssigneeIds] = useState(assigneeIdsArray);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({
    display: false,
    severity: "error",
    message: "",
  });

  useEffect(() => {
    setDateFrom(dateFromValue);
  }, [dateFromValue]);

  useEffect(() => {
    setDateTo(dateToValue);
  }, [dateToValue]);

  useEffect(() => {
    setAssigneeIds(assigneeIdsArray);
  }, [assigneeIdsArray]);

  const hideAlert = () =>
    setAlert((a) => ({
      ...a,
      display: false,
    }));

  /**
   * @param {string} url
   * @returns {string}
   * @throws {Error}
   */
  const extractSpreadsheetIdFromUrl = (url) => {
    const re =
      /(https?:\/\/)?docs\.google\.com\/spreadsheets\/d\/([^/]+)(\/.*)?/i;
    if (!re.test(url)) {
      throw new Error("Invalid Google Spreadsheet URL format");
    }
    return url.replace(re, "$2");
  };

  const waitExport = async (spreadsheetId) => {
    const timeout = (delay) =>
      new Promise((resolve) => setTimeout(resolve, delay));

    while (true) {
      await timeout(1000 * 10);

      try {
        const response = await axios.get(
          `/api/v1/asana-time-tracking/export/${spreadsheetId}`,
        );

        if (response.data["status"] === "failed") {
          throw new Error(response.data["message"]);
        }
        if (response.data["status"] !== "processing") {
          break;
        }
      } catch (e) {
        if (e.response && e.response.status === 404) {
          continue;
        }
        throw e;
      }
    }
  };

  const handleFormSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      try {
        if (!spreadsheetUrl.length) {
          throw new Error("Invalid form data");
        }

        const spreadsheetId = extractSpreadsheetIdFromUrl(spreadsheetUrl);

        hideAlert();
        setIsLoading(true);

        let url = "";
        switch (type) {
          case "user-sla":
            url = "/api/v1/user-sla/export";
            break;
          default:
            url = "/api/v1/tasks/sla/work_intervals/export";
            break;
        }

        await axios.post(url, {
          google_spreadsheet_id: spreadsheetId,
          date_from: dateFrom,
          date_to: dateTo,
          assignee_id: assigneeIds,
        });
        await waitExport(spreadsheetId);

        setAlert({
          display: true,
          severity: "success",
          message: "Data is exported",
        });
      } catch (e) {
        setAlert({
          display: true,
          severity: "error",
          message: e.response ? e.response.data.message : e.message,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [dateFrom, dateTo, spreadsheetUrl, assigneeIds],
  );

  return (
    <div className="work-time-sla-export">
      <Container>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleFormSubmit}
        >
          <Collapse in={alert.display}>
            <Alert
              severity={alert.severity}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={hideAlert}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{ mb: 2 }}
            >
              {alert.message}
            </Alert>
          </Collapse>
          <TextField
            label="Google Spreadsheet URL"
            variant="outlined"
            required
            fullWidth
            margin="normal"
            value={spreadsheetUrl}
            onChange={(e) => {
              setSpreadsheetUrl(e.target.value);
            }}
            disabled={isLoading}
          />
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {isLoading && <CircularProgress sx={{ mr: 1 }} />}
            <Button
              size="medium"
              variant="contained"
              endIcon={<ImportExportIcon />}
              disabled={isLoading}
              type="submit"
            >
              Export
            </Button>
          </Box>
        </Box>
        <Box sx={{ mt: 3 }}>
          <div variant="body2" display="block">
            Before exporting, share the Google Document with the user{" "}
            <EmailChip
              email="energame-bad-main@energame-bad-services.iam.gserviceaccount.com"
              size="small"
            />
            , and grant the "Editor" role.
          </div>
          <Typography variant="body2" display="block">
            Data will be inserted in the last sheet in document.
          </Typography>
        </Box>
      </Container>
    </div>
  );
}
